import { Container, formatDate, Separator } from '@amelio/ui-library';

import { BelowThresholdResults } from './BelowThresholdResults/BelowThresholdResults';
import {
    getBelowThresholdState,
    getResultsStatus,
    type BelowThresholdState,
} from './BelowThresholdResults/belowThresholdState';
import { ResultsStatePill } from './BelowThresholdResults/ResultsStatePill';
import { EmptyResults } from './EmptyResults/EmptyResults';
import { ResultsContent } from './ResultsContent/ResultsContent';
import { ResultsHeader } from './ResultsHeader/ResultsHeader';

import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { FeedbackResultsArrowIllustration } from '@/components/Illustrations/Illustration';
import {
    QuestionResultContainer,
    type QuestionResultData,
} from '@/components/QuestionResultContainer';
import { QuestionType } from '@/constants/QuestionType';
import { ReviewType } from '@/constants/review/CycleInstance';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import i18n from '@/plugins/i18n';
import { getReviewTypeLabel, hasRespondents } from '@/utils/cycleInstance';
import { getFullName } from '@/utils/user';

export interface ResultsProps {
    reviewInstance: ReviewInstance;
    reviewee: User;
    reviewers: User[];
    questions: QuestionResultData[];
    onReplySubmit?: (reviewQuestionAnswerId: string, content: string) => Promise<void> | void;
    isSubmittingReply?: boolean;
}

export const Results = ({
    reviewInstance,
    reviewee,
    reviewers,
    questions,
    onReplySubmit,
    isSubmittingReply = false,
}: ResultsProps) => {
    const { getJobTitle } = useCompanyInformation();
    const reviewType = reviewInstance.cycleInstance?.reviewType ?? ReviewType.EVALUATION_360;
    const belowThreshold = getBelowThresholdState(reviewInstance, i18n.language);
    const status = getResultsStatus(reviewInstance, belowThreshold, i18n.language);

    return (
        <Container
            direction={'col'}
            className={'p-sm-18 w-full p-10'} // p-18 intentional: between '2xl' (p-16) and 'page' (p-22)
        >
            <div className={'relative'}>
                <ResultsHeader
                    employeeName={getFullName(reviewee)}
                    employeeRole={getJobTitle(reviewee.jobTitleId || '')}
                    evaluationLabel={getReviewTypeLabel(reviewType)}
                    invitedCount={reviewInstance.totalCount}
                    respondedCount={reviewInstance.responseCount}
                    reviewers={reviewers}
                    dateLabel={
                        reviewInstance.completedAt
                            ? formatDate({
                                  date: reviewInstance.completedAt,
                                  language: i18n.language,
                              })
                            : undefined
                    }
                    reviewType={reviewType}
                    feedbackPrivacy={
                        hasRespondents(reviewType) ? reviewInstance.feedbackPrivacy : undefined
                    }
                    commentRepliesEnabled={reviewInstance.commentRepliesEnabled}
                    minimumResponses={reviewInstance.minimumResponses}
                    status={status && <ResultsStatePill status={status} />}
                />

                {/* Points at the answers, so it is left out while they are withheld. Starts below the header card:
                    drawn any higher, it crosses the card's last line of text. */}
                {!belowThreshold && (
                    <FeedbackResultsArrowIllustration
                        className={'relative left-29 -mb-42 sm:left-20 md:left-75 xl:left-105'}
                    />
                )}
            </div>

            <ResultsBody
                belowThreshold={belowThreshold}
                reviewInstance={reviewInstance}
                reviewee={reviewee}
                questions={questions}
                onReplySubmit={onReplySubmit}
                isSubmittingReply={isSubmittingReply}
            />
        </Container>
    );
};

/** What the results show below their header: nothing yet, nothing at all, or the answers. */
const ResultsBody = ({
    belowThreshold,
    reviewInstance,
    reviewee,
    questions,
    onReplySubmit,
    isSubmittingReply = false,
}: Omit<ResultsProps, 'reviewers'> & { belowThreshold: BelowThresholdState | null }) => {
    const reviewType = reviewInstance.cycleInstance?.reviewType ?? ReviewType.EVALUATION_360;

    if (belowThreshold) {
        return (
            <BelowThresholdResults
                state={belowThreshold}
                revieweeId={reviewee.id}
            />
        );
    }

    if (questions.length < 1) {
        return (
            <EmptyResults
                reviewType={reviewType}
                employeeFirstName={reviewee.firstName}
            />
        );
    }

    return (
        <ResultsContent>
            {questions.map((questionData, index) => {
                const questionId = questionData.question.id as string;
                const isLastQuestion = index === questions.length - 1;

                if (questionData.question.questionType === QuestionType.SECTION_TITLE) {
                    return null;
                }

                return (
                    <div key={questionId}>
                        <QuestionResultContainer
                            data={questionData}
                            onReplySubmit={onReplySubmit}
                            isSubmittingReply={isSubmittingReply}
                            areRepliesClosed={!reviewInstance.commentRepliesEnabled}
                            is360={reviewType === ReviewType.EVALUATION_360}
                            reviewee={reviewee}
                        />

                        {!isLastQuestion && (
                            <Separator
                                spacing={'xl'}
                                color={'base-200'}
                                dataTestId={`results-separator-${index}`}
                            />
                        )}
                    </div>
                );
            })}
        </ResultsContent>
    );
};

export default Results;
